import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType,
  ogTitle,
  ogDescription,
  schema
}: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Add Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema);
    }

    // Update last-modified
    const lastModified = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', lastModified);
  }, [title, description, keywords, canonical, ogType, ogTitle, ogDescription, schema]);
};

// Schema.org generators
export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://abrilweldworkspro.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#organization`,
    name: 'April Weld Works',
    image: `${siteUrl}/vite.svg`,
    description: 'Industrial welding and metal fabrication services in Atlanta, GA and Metro Atlanta. Structural steel, MIG/TIG welding, repairs and ornamental metal.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4055 Brushymill ct',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30052',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 33.7490,
      longitude: -84.3880
    },
    telephone: '+16786931234',
    email: 'abrilweldworkspro@gmail.com',
    url: siteUrl,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '14:00'
      }
    ],
    priceRange: '$$',
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 33.7490,
        longitude: -84.3880
      },
      geoRadius: '50000'
    }
  };
};

export const generateWebPageSchema = (pageName: string, pageDescription: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://abrilweldworkspro.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/#webpage`,
    url: siteUrl,
    name: pageName,
    description: pageDescription,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'April Weld Works'
    },
    about: {
      '@id': `${siteUrl}/#organization`
    },
    inLanguage: 'es-US'
  };
};

export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://abrilweldworkspro.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Industrial Welding and Metal Fabrication',
    provider: {
      '@id': `${siteUrl}/#organization`
    },
    areaServed: {
      '@type': 'City',
      name: 'Atlanta',
      containedIn: {
        '@type': 'State',
        name: 'Georgia'
      }
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Welding Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'MIG/TIG Welding',
            description: 'Professional welding with MIG and TIG techniques for industrial and commercial projects'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Metal Fabrication',
            description: 'Custom metal structure design and fabrication'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Industrial Repair',
            description: 'Industrial equipment repair and maintenance services'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Ornamental Metal',
            description: 'Custom ornamental metal work and decorative metalwork'
          }
        }
      ]
    }
  };
};

export const generateContactPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://abrilweldworkspro.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${siteUrl}/contact#webpage`,
    url: `${siteUrl}/contact`,
    name: 'Contact - April Weld Works',
    description: 'Contact us for industrial welding and metal fabrication services in Atlanta, GA',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`
    },
    about: {
      '@id': `${siteUrl}/#organization`
    },
    inLanguage: 'es-US'
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://abrilweldworkspro.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};
